import { useState, useRef } from "react";
import { importAdminUsers } from "../../../../Actions/AdminActions";
import { formatApiError } from "../../../../Actions/Utils";

const USER_TYPE_OPTIONS = ["Internal", "External - User", "External - Stakeholder"];

const EXAMPLE_CSV = `phone,name,gender,state,district,sector,block,cadre,kb
9876543210,Alice,female,Maharashtra,Pune,Shivaji Nagar,Block1,AWW,WCD
9876543211,Bob,male,Maharashtra,Mumbai,Andheri East,Block2,ASHA,NHSRC`;

function StatusBadge({ status }) {
  const map = {
    success: "bg-green-100 text-green-700",
    created: "bg-green-100 text-green-700",
    skipped: "bg-yellow-100 text-yellow-700",
    duplicate: "bg-yellow-100 text-yellow-700",
    error: "bg-red-100 text-red-700",
    failed: "bg-red-100 text-red-700",
  };
  const key = (status || "").toLowerCase();
  const color = map[key] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {status || "—"}
    </span>
  );
}

function DropZone({ onFile, file }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith(".csv")) onFile(f);
  }

  function handleChange(e) {
    const f = e.target.files[0];
    if (f) onFile(f);
    e.target.value = "";
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current.click()}
      className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 sm:p-10 cursor-pointer transition-colors ${
        dragging
          ? "border-indigo-400 bg-indigo-50"
          : file
          ? "border-green-400 bg-green-50"
          : "border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/30"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleChange}
      />
      {file ? (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium text-gray-800">{file.name}</p>
          <p className="text-xs text-gray-500 mt-1">
            {(file.size / 1024).toFixed(1)} KB — click to change
          </p>
        </>
      ) : (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <p className="text-sm font-medium text-gray-700">
            Drop a CSV file here or <span className="text-indigo-600">browse</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">Only .csv files, up to 500 rows</p>
        </>
      )}
    </div>
  );
}

function SummaryBar({ results }) {
  if (!results.length) return null;
  const counts = results.reduce(
    (acc, r) => {
      const k = (r.status || "").toLowerCase();
      if (k === "success" || k === "created") acc.success++;
      else if (k === "skipped" || k === "duplicate") acc.skipped++;
      else acc.error++;
      return acc;
    },
    { success: 0, skipped: 0, error: 0 }
  );
  return (
    <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
      {[
        { label: "Success", count: counts.success, color: "text-green-600" },
        { label: "Skipped / Duplicate", count: counts.skipped, color: "text-yellow-600" },
        { label: "Error", count: counts.error, color: "text-red-600" },
      ].map(({ label, count, color }) => (
        <div key={label} className="text-sm">
          <span className={`font-semibold ${color}`}>{count}</span>
          <span className="text-gray-500 ml-1">{label}</span>
        </div>
      ))}
    </div>
  );
}

export default function BulkImport() {
  const [file, setFile] = useState(null);
  const [userType, setUserType] = useState("External - User");
  const [approach, setApproach] = useState("PAGE_SEARCH");
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [globalError, setGlobalError] = useState("");
  const [ran, setRan] = useState(false);
  const [showExample, setShowExample] = useState(false);

  function downloadExample() {
    const blob = new Blob([EXAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_import_example.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleImport() {
    if (!file) return;
    setLoading(true);
    setGlobalError("");
    setResults([]);
    setRan(false);

    try {
      const res = await importAdminUsers(file, dryRun, { user_type: userType, approach });
      setRan(true);

      if (res?.error) {
        setGlobalError(
          res.error === "Failed to fetch"
            ? "Could not reach the API server. Ensure the backend is running and allows requests from this origin."
            : res.error
        );
        return;
      }

      if (res?.status === 200 || res?.status === 207) {
        const data = res.data;
        const rows = Array.isArray(data)
          ? data
          : data?.results || data?.rows || data?.data || [];
        setResults(rows);
      } else {
        setGlobalError(
          formatApiError(res?.data?.detail) ||
          res?.data?.message ||
          (typeof res?.data === "string" ? res.data : "") ||
          "Import failed. Please check your file and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setFile(null);
    setUserType("External - User");
    setApproach("PAGE_SEARCH");
    setResults([]);
    setGlobalError("");
    setRan(false);
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Bulk Import</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Upload a CSV file to create multiple users at once. Up to 500 rows per file.
        </p>
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User Type *</label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              {USER_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approach *</label>
            <input
              type="text"
              required
              placeholder="e.g. PAGE_SEARCH"
              value={approach}
              onChange={(e) => setApproach(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <DropZone onFile={setFile} file={file} />

        <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4">
          {/* Dry-run toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => setDryRun((v) => !v)}
              className={`relative w-10 h-6 rounded-full transition-colors ${
                dryRun ? "bg-indigo-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  dryRun ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Dry Run</p>
              <p className="text-xs text-gray-500">
                {dryRun
                  ? "Validate only — no users will be created"
                  : "Live import — users will be created"}
              </p>
            </div>
          </label>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={downloadExample}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Example CSV
            </button>
            {ran && (
              <button
                type="button"
                onClick={reset}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            )}
            <button
              type="button"
              disabled={!file || !approach.trim() || loading}
              onClick={handleImport}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                dryRun
                  ? "bg-amber-500 hover:bg-amber-600"
                  : "bg-indigo-600 hover:bg-indigo-700"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing…
                </>
              ) : dryRun ? (
                "Validate CSV"
              ) : (
                "Import Users"
              )}
            </button>
          </div>
        </div>

        {/* Format help */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => setShowExample((v) => !v)}
            className="text-xs text-indigo-600 hover:underline"
          >
            {showExample ? "Hide" : "Show"} expected CSV format
          </button>
          {showExample && (
            <div className="mt-2 overflow-x-auto">
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-600 whitespace-pre">
                {EXAMPLE_CSV}
              </pre>
              <p className="mt-2 text-xs text-gray-500">
                Mandatory fields: <strong>phone</strong>, <strong>name</strong>, <strong>cadre</strong>, <strong>kb</strong>. Optional: gender, state, district, sector (address), block. User type and approach are set above and apply to all imported users.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Global error */}
      {globalError && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {globalError}
        </div>
      )}

      {/* Results */}
      {ran && results.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-700">
                {dryRun ? "Validation Results" : "Import Results"} — {results.length} rows
              </h2>
              {dryRun && results.length > 0 && (
                <p className="text-xs text-amber-600 font-medium">
                  Dry run — no changes made. Toggle off and re-import to create users.
                </p>
              )}
            </div>
            <SummaryBar results={results} />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {["Row", "Phone", "Status", "Message"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {results.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-500 whitespace-nowrap">
                      {r.row ?? i + 1}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono whitespace-nowrap">
                      {r.phone || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatApiError(r.message) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ran && results.length === 0 && !globalError && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 py-12 text-center text-gray-400 text-sm">
          No results returned from the server.
        </div>
      )}
    </div>
  );
}
